import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Key, Plus, Trash2, Copy, Eye, EyeOff, Check, 
  Activity, FileText, Code, Book, AlertTriangle,
  Loader2, RefreshCw, Clock, Globe, Shield, Zap,
  ChevronRight, ExternalLink, Terminal
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ApiDashboard = () => {
  const [activeTab, setActiveTab] = useState('keys');
  const [apiKeys, setApiKeys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showSecret, setShowSecret] = useState({});
  const [newKey, setNewKey] = useState({
    name: '',
    description: '',
    rate_limit: 100,
    expires_days: null
  });
  const [createdKey, setCreatedKey] = useState(null);

  useEffect(() => {
    fetchApiKeys();
    fetchStats();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/public-api/keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.keys || []);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/public-api/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const createApiKey = async () => {
    if (!newKey.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      setCreating(true);
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/public-api/keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newKey)
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedKey(data);
        toast.success('API Key creada exitosamente');
        fetchApiKeys();
        setNewKey({ name: '', description: '', rate_limit: 100, expires_days: null });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear API key');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setCreating(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!window.confirm('¿Estás seguro de revocar esta API key? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/public-api/keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('API Key revocada');
        fetchApiKeys();
      } else {
        toast.error('Error al revocar API key');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Code className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">API Pública</h1>
              <p className="text-slate-600">Plan Empresarial - Integra facturación en tus aplicaciones</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_invoices_via_api || 0}</p>
                    <p className="text-sm text-slate-500">Facturas totales</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.monthly_invoices || 0}</p>
                    <p className="text-sm text-slate-500">Este mes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Key className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.active_api_keys || 0}</p>
                    <p className="text-sm text-slate-500">API Keys activas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_api_requests || 0}</p>
                    <p className="text-sm text-slate-500">Requests totales</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex overflow-x-auto">
            <TabsTrigger value="keys" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              API Keys
            </TabsTrigger>
            <TabsTrigger value="docs" className="flex items-center gap-2">
              <Book className="w-4 h-4" />
              Documentación
            </TabsTrigger>
            <TabsTrigger value="examples" className="flex items-center gap-2">
              <Terminal className="w-4 h-4" />
              Ejemplos
            </TabsTrigger>
          </TabsList>

          {/* API Keys Tab */}
          <TabsContent value="keys" className="space-y-6">
            {/* Created Key Alert */}
            {createdKey && (
              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-900 mb-2">¡API Key creada exitosamente!</h3>
                      <div className="bg-white rounded-lg p-4 space-y-3">
                        <div>
                          <Label className="text-xs text-slate-500">API Key</Label>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-slate-100 px-3 py-2 rounded text-sm font-mono break-all">
                              {createdKey.api_key}
                            </code>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => copyToClipboard(createdKey.api_key, 'API Key')}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">API Secret</Label>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-red-50 border border-red-200 px-3 py-2 rounded text-sm font-mono break-all text-red-700">
                              {createdKey.api_secret}
                            </code>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => copyToClipboard(createdKey.api_secret, 'API Secret')}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                          <p className="text-sm">
                            <strong>¡Guarda el API Secret ahora!</strong> No podrás verlo de nuevo.
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => setCreatedKey(null)}
                      >
                        Entendido, ya lo guardé
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Create New Key */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Crear Nueva API Key
                </CardTitle>
                <CardDescription>
                  Genera credenciales para integrar facturación en tus aplicaciones
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Nombre *</Label>
                    <Input 
                      placeholder="Ej: Producción, Desarrollo, Mi App"
                      value={newKey.name}
                      onChange={(e) => setNewKey(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Input 
                      placeholder="Descripción opcional"
                      value={newKey.description}
                      onChange={(e) => setNewKey(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Rate Limit (requests/min)</Label>
                    <Input 
                      type="number"
                      value={newKey.rate_limit}
                      onChange={(e) => setNewKey(prev => ({ ...prev, rate_limit: parseInt(e.target.value) || 100 }))}
                    />
                  </div>
                  <div>
                    <Label>Expiración (días)</Label>
                    <Input 
                      type="number"
                      placeholder="Sin expiración"
                      value={newKey.expires_days || ''}
                      onChange={(e) => setNewKey(prev => ({ 
                        ...prev, 
                        expires_days: e.target.value ? parseInt(e.target.value) : null 
                      }))}
                    />
                  </div>
                </div>
                <Button onClick={createApiKey} disabled={creating || !newKey.name.trim()}>
                  {creating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</>
                  ) : (
                    <><Plus className="w-4 h-4 mr-2" /> Crear API Key</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Existing Keys */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Mis API Keys</CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchApiKeys}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : apiKeys.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Key className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No tienes API keys creadas</p>
                    <p className="text-sm">Crea una para comenzar a integrar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {apiKeys.map((key) => (
                      <div 
                        key={key.id}
                        className={`border rounded-lg p-4 ${
                          key.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{key.name}</h4>
                              {key.is_active ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                  Activa
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                  Revocada
                                </span>
                              )}
                            </div>
                            {key.description && (
                              <p className="text-sm text-slate-500 mb-2">{key.description}</p>
                            )}
                            <div className="flex items-center gap-2 mb-2">
                              <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">
                                {key.api_key}
                              </code>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={() => copyToClipboard(key.api_key, 'API Key')}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                {key.usage_count || 0} requests
                              </span>
                              <span className="flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                {key.rate_limit}/min
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Creada: {formatDate(key.created_at)}
                              </span>
                            </div>
                          </div>
                          {key.is_active && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => revokeApiKey(key.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documentation Tab */}
          <TabsContent value="docs" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Book className="w-5 h-5" />
                  Documentación de la API
                </CardTitle>
                <CardDescription>
                  Guía completa para integrar facturación electrónica en tus aplicaciones
                </CardDescription>
              </CardHeader>
              <CardContent className="prose max-w-none">
                {/* Base URL */}
                <div className="bg-slate-900 text-slate-100 rounded-lg p-4 mb-6">
                  <p className="text-sm text-slate-400 mb-1">Base URL</p>
                  <code className="text-green-400 text-lg">{API_URL}/api/v1</code>
                </div>

                {/* Authentication */}
                <h3 className="text-lg font-semibold mt-6 mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-500" />
                  Autenticación
                </h3>
                <p className="text-slate-600 mb-3">
                  Todas las solicitudes deben incluir los headers de autenticación:
                </p>
                <div className="bg-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre className="text-slate-700">{`X-API-Key: tu_api_key
X-API-Secret: tu_api_secret`}</pre>
                </div>

                {/* Create Invoice Endpoint */}
                <h3 className="text-lg font-semibold mt-8 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-500" />
                  Crear Factura
                </h3>
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full inline-flex items-center gap-2 text-sm font-medium mb-3">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  POST /invoices
                </div>
                
                <h4 className="font-medium mt-4 mb-2">Request Body</h4>
                <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre>{`{
  "receptor_rfc": "XAXX010101000",
  "receptor_name": "Cliente Ejemplo SA de CV",
  "receptor_email": "cliente@ejemplo.com",
  "receptor_tax_regime": "601",
  "receptor_zip_code": "06600",
  "receptor_cfdi_use": "G03",
  "items": [
    {
      "description": "Servicio de consultoría",
      "quantity": 1,
      "unit_price": 5000.00,
      "product_code": "80141600",
      "unit_code": "E48",
      "tax_included": true
    }
  ],
  "payment_form": "03",
  "payment_method": "PUE",
  "currency": "MXN",
  "notes": "Factura generada vía API",
  "callback_url": "https://tu-servidor.com/webhook"
}`}</pre>
                </div>

                <h4 className="font-medium mt-4 mb-2">Campos del Item</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3">Campo</th>
                        <th className="text-left p-3">Tipo</th>
                        <th className="text-left p-3">Requerido</th>
                        <th className="text-left p-3">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="p-3 font-mono text-xs">description</td>
                        <td className="p-3">string</td>
                        <td className="p-3">✅</td>
                        <td className="p-3">Descripción del producto/servicio</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono text-xs">quantity</td>
                        <td className="p-3">number</td>
                        <td className="p-3">❌</td>
                        <td className="p-3">Cantidad (default: 1)</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono text-xs">unit_price</td>
                        <td className="p-3">number</td>
                        <td className="p-3">✅</td>
                        <td className="p-3">Precio unitario</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono text-xs">product_code</td>
                        <td className="p-3">string</td>
                        <td className="p-3">❌</td>
                        <td className="p-3">Clave SAT (default: 80141600)</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono text-xs">unit_code</td>
                        <td className="p-3">string</td>
                        <td className="p-3">❌</td>
                        <td className="p-3">Clave unidad SAT (default: E48)</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono text-xs">tax_included</td>
                        <td className="p-3">boolean</td>
                        <td className="p-3">❌</td>
                        <td className="p-3">Si el precio incluye IVA (default: true)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4 className="font-medium mt-4 mb-2">Response (200 OK)</h4>
                <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre>{`{
  "success": true,
  "invoice_id": "uuid-de-la-factura",
  "cfdi_uuid": "UUID-DEL-CFDI-TIMBRADO",
  "status": "stamped",
  "subtotal": 4310.34,
  "iva": 689.66,
  "total": 5000.00,
  "pdf_url": "/api/public/invoices/{id}/pdf",
  "xml_url": "/api/public/invoices/{id}/xml"
}`}</pre>
                </div>

                {/* Get Invoice Endpoint */}
                <h3 className="text-lg font-semibold mt-8 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Obtener Factura
                </h3>
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full inline-flex items-center gap-2 text-sm font-medium mb-3">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  GET /invoices/{'{invoice_id}'}
                </div>

                <h4 className="font-medium mt-4 mb-2">Response (200 OK)</h4>
                <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre>{`{
  "success": true,
  "invoice": {
    "id": "uuid-de-la-factura",
    "cfdi_uuid": "UUID-DEL-CFDI",
    "status": "stamped",
    "client_rfc": "XAXX010101000",
    "client_name": "Cliente Ejemplo SA de CV",
    "total": 5000.00,
    "created_at": "2026-04-19T15:30:00Z"
  }
}`}</pre>
                </div>

                {/* Error Codes */}
                <h3 className="text-lg font-semibold mt-8 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Códigos de Error
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3">Código</th>
                        <th className="text-left p-3">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="p-3 font-mono">401</td>
                        <td className="p-3">API Key inválida o falta autenticación</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono">400</td>
                        <td className="p-3">Datos de factura inválidos</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono">404</td>
                        <td className="p-3">Factura no encontrada</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono">429</td>
                        <td className="p-3">Rate limit excedido</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-mono">500</td>
                        <td className="p-3">Error interno del servidor</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Webhooks */}
                <h3 className="text-lg font-semibold mt-8 mb-3 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-500" />
                  Webhooks
                </h3>
                <p className="text-slate-600 mb-3">
                  Si proporcionas un <code className="bg-slate-100 px-1 rounded">callback_url</code>, 
                  recibirás notificaciones cuando la factura sea procesada:
                </p>
                <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre>{`POST tu-callback-url
Content-Type: application/json

{
  "event": "invoice.created",
  "invoice_id": "uuid-de-la-factura",
  "cfdi_uuid": "UUID-DEL-CFDI",
  "status": "stamped",
  "total": 5000.00
}`}</pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Examples Tab */}
          <TabsContent value="examples" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Terminal className="w-5 h-5" />
                  Ejemplos de Código
                </CardTitle>
                <CardDescription>
                  Implementaciones en diferentes lenguajes de programación
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="curl" className="mt-4">
                  <TabsList>
                    <TabsTrigger value="curl">cURL</TabsTrigger>
                    <TabsTrigger value="python">Python</TabsTrigger>
                    <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                    <TabsTrigger value="php">PHP</TabsTrigger>
                  </TabsList>

                  <TabsContent value="curl" className="mt-4">
                    <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <pre>{`curl -X POST "${API_URL}/api/v1/invoices" \\
  -H "X-API-Key: tu_api_key" \\
  -H "X-API-Secret: tu_api_secret" \\
  -H "Content-Type: application/json" \\
  -d '{
    "receptor_rfc": "XAXX010101000",
    "receptor_name": "Cliente SA de CV",
    "receptor_email": "cliente@email.com",
    "receptor_tax_regime": "601",
    "receptor_zip_code": "06600",
    "items": [{
      "description": "Servicio de consultoría",
      "unit_price": 5000.00
    }]
  }'`}</pre>
                    </div>
                  </TabsContent>

                  <TabsContent value="python" className="mt-4">
                    <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <pre>{`import requests

API_URL = "${API_URL}/api/v1"
API_KEY = "tu_api_key"
API_SECRET = "tu_api_secret"

headers = {
    "X-API-Key": API_KEY,
    "X-API-Secret": API_SECRET,
    "Content-Type": "application/json"
}

data = {
    "receptor_rfc": "XAXX010101000",
    "receptor_name": "Cliente SA de CV",
    "receptor_email": "cliente@email.com",
    "receptor_tax_regime": "601",
    "receptor_zip_code": "06600",
    "items": [{
        "description": "Servicio de consultoría",
        "unit_price": 5000.00
    }]
}

response = requests.post(
    f"{API_URL}/invoices",
    headers=headers,
    json=data
)

result = response.json()
print(f"Factura creada: {result['cfdi_uuid']}")`}</pre>
                    </div>
                  </TabsContent>

                  <TabsContent value="javascript" className="mt-4">
                    <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <pre>{`const API_URL = "${API_URL}/api/v1";
const API_KEY = "tu_api_key";
const API_SECRET = "tu_api_secret";

async function createInvoice() {
  const response = await fetch(\`\${API_URL}/invoices\`, {
    method: "POST",
    headers: {
      "X-API-Key": API_KEY,
      "X-API-Secret": API_SECRET,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      receptor_rfc: "XAXX010101000",
      receptor_name: "Cliente SA de CV",
      receptor_email: "cliente@email.com",
      receptor_tax_regime: "601",
      receptor_zip_code: "06600",
      items: [{
        description: "Servicio de consultoría",
        unit_price: 5000.00
      }]
    })
  });

  const result = await response.json();
  console.log(\`Factura creada: \${result.cfdi_uuid}\`);
  return result;
}

createInvoice();`}</pre>
                    </div>
                  </TabsContent>

                  <TabsContent value="php" className="mt-4">
                    <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <pre>{`<?php

$api_url = "${API_URL}/api/v1";
$api_key = "tu_api_key";
$api_secret = "tu_api_secret";

$data = [
    "receptor_rfc" => "XAXX010101000",
    "receptor_name" => "Cliente SA de CV",
    "receptor_email" => "cliente@email.com",
    "receptor_tax_regime" => "601",
    "receptor_zip_code" => "06600",
    "items" => [[
        "description" => "Servicio de consultoría",
        "unit_price" => 5000.00
    ]]
];

$ch = curl_init("$api_url/invoices");
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        "X-API-Key: $api_key",
        "X-API-Secret: $api_secret",
        "Content-Type: application/json"
    ],
    CURLOPT_POSTFIELDS => json_encode($data)
]);

$response = curl_exec($ch);
$result = json_decode($response, true);

echo "Factura creada: " . $result['cfdi_uuid'];

curl_close($ch);
?>`}</pre>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* SDK Coming Soon */}
            <Card className="border-dashed border-2 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Code className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900">SDK Oficial - Próximamente</h3>
                    <p className="text-blue-700 text-sm">
                      Estamos desarrollando SDKs oficiales para Python, JavaScript y PHP. 
                      ¡Pronto disponibles!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ApiDashboard;
